import type { ProviderKind } from '@hangar/shared';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { createTokenCredential, rotateTokenCredential } from '@/api/endpoints';
import { useProviderAccountMutations, useProviderAccounts } from '@/api/hooks';
import type { ProviderAccount } from '@/api/schemas';
import { credentialsMissingStatus } from './credentialModel';
import { CONNECT_COPY, replaceTokenError } from './providerAccountModel';
import { useSubmitState, type SubmitState } from './useSubmitState';
import { useVerifyProviderToken } from './useVerifyProviderToken';

/** What the dialog says about a failed replace: not served yet, a stale etag (412), or the API's own problem. */
function replaceProblem(err: unknown): unknown {
  if (err instanceof ApiError && credentialsMissingStatus(err.status)) return new Error('Not available yet: this Hangar cannot store tokens (POST /v1/credentials); the account keeps its current token.');
  return err instanceof ApiError && err.status === 412 ? new Error('The account changed since this page loaded. It was reloaded: save again.') : err;
}

interface ApplyDeps {
  account: ProviderAccount;
  /** The account already had a stored credential when the dialog opened. */
  rotating: boolean;
  token: string;
  created: string | null;
  onCreated: (credentialId: string) => void;
  attach: (credentialId: string) => Promise<unknown>;
}

/** Rotates the stored credential in place, or stores a new one and attaches it to the account. */
async function applyToken({ account, rotating, token, created, onCreated, attach }: ApplyDeps): Promise<void> {
  if (account.credentialId && rotating) {
    await rotateTokenCredential(account.credentialId, token.trim());
    return;
  }
  let credentialId = created;
  if (!credentialId) {
    const kind = CONNECT_COPY[account.kind === 'bitrise' ? 'bitrise' : 'eas'].credentialKind;
    credentialId = (await createTokenCredential(kind, `${account.name} token`, token.trim(), crypto.randomUUID())).id;
    onCreated(credentialId);
  }
  await attach(credentialId);
}

/** Validate, run, and show the problem of a failed replace. */
async function submitReplace(sub: SubmitState, invalid: boolean, run: () => Promise<void>): Promise<void> {
  sub.setSubmitted(true);
  sub.setProblem(null);
  if (invalid) return;
  sub.setBusy(true);
  try {
    await run();
  } catch (err) {
    sub.setProblem(replaceProblem(err));
  } finally {
    sub.setBusy(false);
  }
}

/**
 * State and submit of the "Replace token" dialog: the new token is verified against the account first (a token the
 * provider refuses, or that cannot build on it, never replaces a working one), then the stored credential is rotated, or
 * one is stored and attached.
 */
export function useReplaceTokenForm(opened: ProviderAccount, onDone: () => void) {
  const { patch } = useProviderAccountMutations();
  // The latest version of the account (a 412 reloads the list; the retry then carries the new etag).
  const account = useProviderAccounts().data?.find((a) => a.id === opened.id) ?? opened;
  const [token, setToken] = useState('');
  /** A credential stored by an earlier attempt whose attach failed: the retry attaches it instead of storing the token twice. */
  const [created, setCreated] = useState<string | null>(null);
  const sub = useSubmitState();
  const tokenError = replaceTokenError(token, Boolean(created));
  const rotating = Boolean(opened.credentialId);
  const kind: ProviderKind = account.kind === 'bitrise' ? 'bitrise' : 'eas';
  const check = useVerifyProviderToken({ kind, token, externalAccount: account.externalAccount ?? '' });

  const run = async () => {
    if (!created && !(check.result ?? (await check.verify())).ok) return;
    // The token is cleared as soon as it is stored: a failed attach must not leave it in the field.
    const onCreated = (credentialId: string) => {
      setCreated(credentialId);
      setToken('');
    };
    const attach = (credentialId: string) => patch.mutateAsync({ id: account.id, body: { credentialId }, etag: account.etag });
    await applyToken({ account, rotating, token, created, attach, onCreated });
    setToken('');
    toast.success(`${account.name}: token ${rotating ? 'replaced' : 'stored'}`, { description: `${CONNECT_COPY[kind].provider} accepted it; delete the old token on ${kind === 'eas' ? 'expo.dev' : 'bitrise.io'}.` });
    onDone();
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    return submitReplace(sub, Boolean(tokenError), run);
  };
  return { account, kind, check, token, setToken, created, error: sub.submitted ? tokenError : undefined, eas: kind === 'eas', onSubmit, problem: sub.problem, busy: sub.busy || check.busy };
}

export type ReplaceTokenForm = ReturnType<typeof useReplaceTokenForm>;
